import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Clock, Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-[hsl(243,65%,20%)] text-white p-12 flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold font-['Manrope']">ClockIn Pro</span>
          </div>
          
          <div>
            <h1 className="text-5xl font-bold font-['Manrope'] leading-tight">
              Check your<br />email
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-md">
              We've sent password reset instructions to your email address. 
              The link will expire in 1 hour.
            </p>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Check your inbox
            </div>
          </div>
        </div>

        {/* Right side - Success Message */}
        <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
          <Card className="w-full max-w-md border-0 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-['Manrope']">Check Your Email</CardTitle>
              <CardDescription className="mt-2">
                If an account exists with <strong>{email}</strong>, you'll receive password reset instructions shortly.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                <div className="bg-slate-100 p-4 rounded-lg text-sm text-slate-600">
                  <p className="font-medium mb-2">Didn't receive the email?</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-500">
                    <li>Check your spam or junk folder</li>
                    <li>Make sure you entered the correct email</li>
                    <li>Wait a few minutes and try again</li>
                  </ul>
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setSubmitted(false)}
                >
                  Try another email
                </Button>
                
                <Link to="/login">
                  <Button className="w-full gap-2 bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[hsl(243,65%,20%)] text-white p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
            <Clock className="w-6 h-6" />
          </div>
          <span className="text-2xl font-bold font-['Manrope']">ClockIn Pro</span>
        </div>
        
        <div>
          <h1 className="text-5xl font-bold font-['Manrope'] leading-tight">
            Forgot your<br />password?
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-md">
            No worries! Enter your email address and we'll send you 
            instructions to reset your password.
          </p>
        </div>
        
        <div className="flex items-center gap-6 text-sm text-white/60">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Password Recovery
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-['Manrope']">Reset Password</CardTitle>
            <CardDescription>
              Enter your email address to receive reset instructions
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <div className="spinner" />
                ) : (
                  'Send Reset Instructions'
                )}
              </Button>
              
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Button>
              </Link>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
